import { TicketArticleUiModel } from '../../shared/ui-models/ticket.ui-model';

/**
 * The ticket thread read like an email thread (CX, 2026-10-19: "only view the most recent
 * update and not the whole thread"). PURE, unit-tested.
 */

/** With more collapsed messages than this before the latest, the middle ones fold into a bar. */
export const FOLD_AFTER = 4;
/** How many collapsed messages stay visible on each side of the fold. */
const KEEP_FIRST = 1;
const KEEP_LAST = 2;

/** One line of a message, as a collapsed row shows it. */
export function preview(body: string, max = 160): string {
  const line = (body || '').replace(/\s+/g, ' ').trim();
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

/** "just now", "5 min ago", "3 hours ago", "2 days ago"; older than a week: nothing. */
export function ago(date: Date, now = Date.now()): string {
  const min = Math.floor((now - date.getTime()) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return d <= 7 ? `${d} day${d === 1 ? '' : 's'} ago` : '';
}

/** The message the thread opens on: the newest one that is a message, not a handoff event. */
export function latestId(articles: TicketArticleUiModel[]): number | undefined {
  for (let i = articles.length - 1; i >= 0; i--) if (!articles[i].isHandoff) return articles[i].id;
  return undefined;
}

export type ThreadRow =
  | { kind: 'article'; article: TicketArticleUiModel }
  | { kind: 'fold'; count: number };

/**
 * What the thread shows, in order. Everything is listed when unfolded (or short); otherwise
 * the first message, an "N older messages" bar, then the last two before the latest, then the
 * latest itself, like Gmail.
 */
export function threadRows(articles: TicketArticleUiModel[], unfolded: boolean): ThreadRow[] {
  const all: ThreadRow[] = articles.map((article) => ({ kind: 'article', article }));
  const before = articles.length - 1; // everything before the latest
  if (unfolded || before <= FOLD_AFTER) return all;
  const hidden = before - KEEP_FIRST - KEEP_LAST;
  return [
    ...all.slice(0, KEEP_FIRST),
    { kind: 'fold', count: hidden },
    ...all.slice(KEEP_FIRST + hidden)
  ];
}
